import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import type { DialogProps } from '@mui/material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';

import Accordion from 'src/components/common/Accordion';

import SelectOrgUnit from './Sections/SelectOrgUnit/SelectOrgUnit';
import SelectOwner from './Sections/SelectOwner/SelectOwner';
import SelectSurvey from './Sections/SelectSurvey/SelectSurvey';
import { CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES } from './constants';
import {
  CreateActionItemProvider,
  useCreateActionItemContext,
} from './CreateActionItemContext';

export const CREATE_ACTION_ITEM_MODAL_TEST_ID = 'create-action-item-modal';

interface CreateActionItemModalContentProps extends DialogProps {
  preselectCurrentSurvey?: boolean;
}
const CreateActionItemModalContent: FC<CreateActionItemModalContentProps> = ({
  preselectCurrentSurvey,
  ...dialogProps
}) => {
  const { t } = useTranslation('myFollowUp');
  const { isValid, confirmCreation } = useCreateActionItemContext();

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      {...dialogProps}
      data-testid={CREATE_ACTION_ITEM_MODAL_TEST_ID}
    >
      <Typography variant="h3">{t('actionItem.create')}</Typography>
      <IconButton
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.primary.main,
        })}
        onClick={(event) => dialogProps.onClose?.(event, 'backdropClick')}
      >
        <CloseIcon fontSize="small" color="primary" />
      </IconButton>

      <DialogContent>
        <Accordion
          initiallyActiveItems={[
            preselectCurrentSurvey
              ? CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES.ORG_UNIT
              : CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES.SURVEY,
          ]}
        >
          <SelectSurvey />
          <SelectOrgUnit />
          <SelectOwner />
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button
          fullWidth
          onClick={(event) => dialogProps.onClose?.(event, 'backdropClick')}
          color="secondary"
          variant="cta"
        >
          {t('actionItem.cancel')}
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disabled={!isValid}
          onClick={confirmCreation}
        >
          {t('actionItem.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface CreateActionItemModalProps extends DialogProps {
  preselectCurrentSurvey?: boolean;
  onConfirm: () => void;
}

const CreateActionItemModal: FC<CreateActionItemModalProps> = ({
  preselectCurrentSurvey,
  onConfirm,
  ...dialogProps
}) => (
  <CreateActionItemProvider
    preselectCurrentSurvey={preselectCurrentSurvey}
    onConfirm={onConfirm}
  >
    <CreateActionItemModalContent
      preselectCurrentSurvey={preselectCurrentSurvey}
      {...dialogProps}
    />
  </CreateActionItemProvider>
);

export default CreateActionItemModal;
